import React from "react";
import { KeyTerm, ImportantNote, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const humanGeographyUnit3Notes: UnitNotes = {
  unitId: "unit-3",
  unitName: "Unit 3: Cultural Patterns and Processes",
  examWeight: "12–17%",
  focus: "Understanding how cultures develop, spread, and interact across space. How cultural traits diffuse and transform.",
  sections: [
    {
      id: "topic-3-1",
      title: "Topic 3.1: Introduction to Culture",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Defining Culture</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Culture is the sum total of knowledge, attitudes, and habitual behavior patterns shared and transmitted by the members of a society. It is the "way of life" of a distinct group of people.
            </p>

            <h4 className="text-lg font-bold text-slate-800 mb-3">Components of Culture</h4>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h5 className="font-bold text-blue-800">🏺 Material Culture (Artifacts)</h5>
                <p className="text-sm text-slate-700 mt-1">The physical, visible things a culture creates.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> Housing, clothing, food, tools, architecture, artwork</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h5 className="font-bold text-purple-800">💭 Non-Material Culture</h5>
                <p className="text-sm text-slate-700 mt-1">The invisible, intangible aspects of culture.</p>
                <div className="space-y-2 mt-2">
                  <p className="text-xs text-slate-600"><strong>Mentifacts:</strong> Ideas, beliefs, values (language, religion, folklore)</p>
                  <p className="text-xs text-slate-600"><strong>Sociofacts:</strong> Institutions and links between individuals (family, politics, education)</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The "Iceberg" Model of Culture</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🌊 Surface Culture</h4>
                <p className="text-sm text-slate-700 mt-1">What is easily observable (food, dress, music).</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">🧊 Deep Culture</h4>
                <p className="text-sm text-slate-700 mt-1">Unconscious rules, concepts of time, personal space, body language, problem-solving roles.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Analyzing Culture: Attitudes</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Geographers analyze how people view cultures different from their own.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">❌ Ethnocentrism</h4>
                <p className="text-sm text-slate-700 mt-1">Judging another culture by the standards of one's own culture. Often leads to belief that one's own culture is superior.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Example:</strong> Viewing eating insects as "disgusting" rather than a protein source</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">✅ Cultural Relativism</h4>
                <p className="text-sm text-slate-700 mt-1">Evaluating a culture by its own standards. Setting aside one's own cultural biases to understand <em>why</em> a group does what they do.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Example:</strong> Understanding eating insects as a sustainable protein source in specific climates</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-2",
      title: "Topic 3.2: Cultural Landscapes",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Cultural Landscape (The Built Environment)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Coined by geographer <strong>Carl Sauer</strong>, the cultural landscape is the "imprint" of human activity on the land. It is how humans modify the physical environment to reflect their cultural identity.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-800">📐 Equation</h4>
              <p className="text-sm text-slate-700 mt-2">Physical Environment + Human Culture = Cultural Landscape</p>
              <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> Agricultural patterns (terracing), religious architecture (mosques, steeples), street signage (bilingual signs), industrial buildings</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Sequent Occupance</h3>
            <KeyTerm 
              term="Sequent Occupance" 
              definition="The notion that successive societies leave their cultural imprints on a place, each contributing to the cumulative cultural landscape."
            />
            <div className="space-y-3 mt-4">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h5 className="font-bold text-purple-800">🎭 Example: New Orleans</h5>
                <p className="text-sm text-slate-700 mt-1">Native American foundations → French architecture → Spanish influence → African cultural contributions → American modern infrastructure</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h5 className="font-bold text-orange-800">🏰 Example: The Alhambra, Spain</h5>
                <p className="text-sm text-slate-700 mt-1">Shows Islamic Moorish architecture converted into a Catholic palace</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Attitudes Toward Ethnicity and Gender in the Landscape</h3>
            <div className="space-y-3">
              <div className="bg-pink-50 p-4 rounded-lg border border-pink-200">
                <h4 className="font-bold text-pink-800">👩 Gendered Spaces</h4>
                <p className="text-sm text-slate-700 mt-1">Certain spaces are designed or culturally designated for specific genders.</p>
                <ul className="text-xs text-slate-600 space-y-1 list-disc pl-4 mt-2">
                  <li><strong>Private vs. Public:</strong> Historically, women in private spaces (homes), men in public (workplaces/government)</li>
                  <li><strong>Modern:</strong> "Pink collar" employment zones or safe transportation cars for women (Tokyo, Mexico City)</li>
                </ul>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🏘️ Ethnic Neighborhoods (Enclaves)</h4>
                <p className="text-sm text-slate-700 mt-1">Areas within a city containing members of the same ethnic background.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Function:</strong> Buffer against discrimination and preserve cultural distinctiveness (Chinatown, Little Italy)</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🌍 Indigenous Land Use</h4>
                <p className="text-sm text-slate-700 mt-1">Traditional landscapes emphasize spiritual connection to land and communal ownership, conflicting with modern capitalist private property lines.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Architecture Styles</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🏠 Traditional Architecture</h4>
                <p className="text-sm text-slate-700 mt-1">Uses locally available materials (wood, brick, stone, mud) and responds to local climate.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> Yurts in Mongolia, Adobe homes in US Southwest</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="font-bold text-gray-800">🏢 Postmodern Architecture</h4>
                <p className="text-sm text-slate-700 mt-1">Popular since 1960s. Emphasizes form over function, uses steel and glass, often looks similar globally (placelessness).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-3",
      title: "Topic 3.3: Cultural Patterns",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Regional Patterns</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              <strong>Culture Region:</strong> An area marked by a culture that distinguishes it from other regions.
            </p>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">📦 Formal Region</h4>
                <p className="text-sm text-slate-700 mt-1">Defined by a government or a uniform cultural trait (e.g., French-speaking region of Canada).</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🎯 Functional Region</h4>
                <p className="text-sm text-slate-700 mt-1">Organized around a focal point (node) (e.g., fan base of a sports team or circulation area of a newspaper).</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">💭 Vernacular (Perceptual) Region</h4>
                <p className="text-sm text-slate-700 mt-1">Defined by people's perceptions (e.g., "The South" in the US).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Sense of Place vs. Placelessness</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">💕 Sense of Place</h4>
                <p className="text-sm text-slate-700 mt-1">The unique emotional attachment people have to a specific location based on its history and culture.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">❌ Placelessness</h4>
                <p className="text-sm text-slate-700 mt-1">The loss of uniqueness of place so one place looks like the next (e.g., strip mall with Walmart and Starbucks looks the same everywhere).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Centripetal and Centrifugal Forces</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🔄 Centripetal Forces (Unify)</h4>
                <p className="text-sm text-slate-700 mt-1">Cultural traits that hold a society together.</p>
                <ul className="text-xs text-slate-600 space-y-1 list-disc pl-4 mt-2">
                  <li>Common language</li>
                  <li>Shared religion</li>
                  <li>National identity (nationalism)</li>
                  <li>Homogenous ethnicity</li>
                </ul>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">💥 Centrifugal Forces (Divide)</h4>
                <p className="text-sm text-slate-700 mt-1">Cultural traits that pull a society apart.</p>
                <ul className="text-xs text-slate-600 space-y-1 list-disc pl-4 mt-2">
                  <li>Multiple competing languages</li>
                  <li>Religious conflicts (Sunni vs. Shia)</li>
                  <li>Ethnic discrimination</li>
                  <li>Uneven economic development</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-4",
      title: "Topic 3.4: Types of Diffusion",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Understanding Diffusion</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              <strong>Diffusion</strong> is the spread of a feature or trend from one place to another over time. The place of origin is the <strong>Hearth</strong>.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Relocation Diffusion</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-bold text-blue-800">🚚 The Spread</h4>
              <p className="text-sm text-slate-700 mt-2">The spread of a cultural trait by people who migrate and carry their cultural traits with them.</p>
              <p className="text-sm text-slate-700 mt-3"><strong>Mechanism:</strong> Physical movement of people</p>
              <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> Pizza brought to US by Italian immigrants; Spanish language in South America via colonization</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Expansion Diffusion</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The spread of a feature from one place to another in a snowballing process. The trait remains strong at the hearth while spreading outward.
            </p>

            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🦠 A. Contagious Diffusion</h4>
                <p className="text-sm text-slate-700 mt-2">Rapid, widespread diffusion throughout the population. Spreads like a disease (person to person) without regard for hierarchy.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> Viral TikTok trends, memes, spread of Islam from Mecca initially</p>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">🏛️ B. Hierarchical Diffusion</h4>
                <p className="text-sm text-slate-700 mt-2">Spread from persons or nodes of authority or power to other persons or places. "Leapfrogs" rural areas initially.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Flow:</strong> Large Cities → Medium Cities → Rural Areas OR Celebrity → Public</p>
                <p className="text-xs text-slate-600 mt-1"><strong>Examples:</strong> High fashion (Paris/Milan to NYC to local malls), architectural styles</p>
                <p className="text-xs text-slate-600 mt-1"><strong>Reverse Hierarchical:</strong> Spreads from bottom up (Walmart starting in rural Arkansas; Hip Hop from the Bronx)</p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🔄 C. Stimulus Diffusion</h4>
                <p className="text-sm text-slate-700 mt-2">The spread of an underlying principle, even though a specific characteristic is rejected. The idea is adopted but modified.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Example:</strong> Maharaja Mac in India (McDonald's burger idea, but vegetable/chicken instead of beef due to Hindu taboos)</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-5",
      title: "Topic 3.5: Historical Causes of Diffusion",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Colonialism and Imperialism</h3>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <p className="text-sm text-slate-700">European powers (Britain, France, Spain, Portugal) colonized the Americas, Africa, and Asia, forcing their culture onto indigenous populations.</p>
              <ul className="text-xs text-slate-600 space-y-1 list-disc pl-4 mt-3">
                <li><strong>Language:</strong> Portuguese in Brazil, English in Nigeria</li>
                <li><strong>Religion:</strong> Missionaries spread Christianity to colonies</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Trade (The Silk Road & Columbian Exchange)</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🗣️ Lingua Franca</h4>
                <p className="text-sm text-slate-700 mt-1">A language mutually understood and commonly used in trade by people with different native languages.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Historical:</strong> Swahili (East Africa), Latin (Europe)</p>
                <p className="text-xs text-slate-600 mt-1"><strong>Modern:</strong> English, Mandarin (business)</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🌍 The Columbian Exchange</h4>
                <p className="text-sm text-slate-700 mt-1">Massive exchange of plants, animals, diseases, and technologies between the Americas and the rest of the world following Columbus's voyages.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Creolization</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The process in which two or more languages converge and form a new language.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">🔤 Pidgin</h4>
                <p className="text-sm text-slate-700 mt-1">A simplified mixture of two languages used for trade. Has no native speakers.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🗨️ Creole Language</h4>
                <p className="text-sm text-slate-700 mt-1">When a pidgin becomes the primary language of a group and develops complex grammar.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> Haitian Creole (French + West African); Gullah Geechee (South Carolina/Georgia)</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-6",
      title: "Topic 3.6: Contemporary Causes of Diffusion",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Globalization and Urbanization</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🌐 Globalization</h4>
                <p className="text-sm text-slate-700 mt-1">The increasing interaction of people, states, and economies. Creates a global culture (Pop Culture).</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🏙️ Urbanization</h4>
                <p className="text-sm text-slate-700 mt-1">People moving to cities creates cultural melting pots where traits diffuse rapidly.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Internet and Time-Space Compression</h3>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h4 className="font-bold text-purple-800">⏱️ Time-Space Compression (Convergence)</h4>
              <p className="text-sm text-slate-700 mt-2">The reduction in time it takes for something to reach another place due to technology (planes, internet). The world feels "smaller."</p>
              <p className="text-sm text-slate-700 mt-3"><strong>Impact:</strong> Culture spreads instantly. An album drops in Seoul (K-Pop) and trends in USA minutes later.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Cultural Convergence vs. Divergence</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🔄 Cultural Convergence</h4>
                <p className="text-sm text-slate-700 mt-1">Cultures become more alike as they increasingly share technology and organizational structures.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Result:</strong> Global Pop Culture (blue jeans, movies, fast food)</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">💥 Cultural Divergence</h4>
                <p className="text-sm text-slate-700 mt-1">Cultures become increasingly dissimilar with the passage of time.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Reason:</strong> Often a reaction against globalization to preserve local identity (Amish, French language laws)</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-7",
      title: "Topic 3.7: Diffusion of Religion and Language",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Language Classification</h3>
            <div className="space-y-3">
              <KeyTerm term="Language Family" definition="Collection of languages related through a common ancestral language (pre-recorded history). Largest: Indo-European." />
              <KeyTerm term="Language Branch" definition="Collection of languages within a family related through a common ancestor (recorded history). Example: Romance, Germanic." />
              <KeyTerm term="Dialect" definition="A regional variety of a language distinguished by vocabulary, spelling, and pronunciation. Isogloss: boundary line between dialects." />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Diffusion of the Indo-European Family</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">⚔️ Nomadic Warrior Theory (Kurgan)</h4>
                <p className="text-sm text-slate-700 mt-1">Spread via military conquest from the Russian Steppes (Kurgan hearth).</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🌾 Sedentary Farmer Theory (Anatolian)</h4>
                <p className="text-sm text-slate-700 mt-1">Spread via diffusion of agricultural techniques from modern-day Turkey.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Classification of Religions</h3>
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🌍 Universalizing Religions</h4>
                <p className="text-sm text-slate-700 mt-2">Attempt to appeal to all people, creating a global scope. Actively seek converts (proselytize).</p>

                <div className="space-y-3 mt-3">
                  <div className="bg-white p-3 rounded border border-blue-100">
                    <h5 className="font-semibold text-blue-800">✝️ Christianity</h5>
                    <p className="text-xs text-slate-600 mt-1"><strong>Hearth:</strong> West Bank/Israel</p>
                    <p className="text-xs text-slate-600"><strong>Diffusion:</strong> Relocation (missionaries), Hierarchical (Emperor Constantine), Contagious</p>
                  </div>
                  <div className="bg-white p-3 rounded border border-blue-100">
                    <h5 className="font-semibold text-blue-800">☪️ Islam</h5>
                    <p className="text-xs text-slate-600 mt-1"><strong>Hearth:</strong> Mecca/Medina (Saudi Arabia)</p>
                    <p className="text-xs text-slate-600"><strong>Diffusion:</strong> Expansion (conquest), Relocation (trade routes to Indonesia)</p>
                  </div>
                  <div className="bg-white p-3 rounded border border-blue-100">
                    <h5 className="font-semibold text-blue-800">☸️ Buddhism</h5>
                    <p className="text-xs text-slate-600 mt-1"><strong>Hearth:</strong> Northern India/Nepal</p>
                    <p className="text-xs text-slate-600"><strong>Diffusion:</strong> Contagious/Hierarchical (Emperor Ashoka), Relocation (to China/Japan)</p>
                  </div>
                  <div className="bg-white p-3 rounded border border-blue-100">
                    <h5 className="font-semibold text-blue-800">🔯 Sikhism</h5>
                    <p className="text-xs text-slate-600 mt-1"><strong>Hearth:</strong> Punjab region (India/Pakistan). Syncretic (Islam + Hinduism)</p>
                  </div>
                </div>
              </div>

              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🏘️ Ethnic Religions</h4>
                <p className="text-sm text-slate-700 mt-2">Appeal primarily to one group of people living in one place. Do not seek converts.</p>

                <div className="space-y-3 mt-3">
                  <div className="bg-white p-3 rounded border border-orange-100">
                    <h5 className="font-semibold text-orange-800">🕉️ Hinduism</h5>
                    <p className="text-xs text-slate-600 mt-1"><strong>Hearth:</strong> Indus River Valley</p>
                    <p className="text-xs text-slate-600"><strong>Diffusion:</strong> Minimal (mostly relocation to UK/US/Diaspora)</p>
                  </div>
                  <div className="bg-white p-3 rounded border border-orange-100">
                    <h5 className="font-semibold text-orange-800">✡️ Judaism</h5>
                    <p className="text-xs text-slate-600 mt-1"><strong>Hearth:</strong> Israel</p>
                    <p className="text-xs text-slate-600"><strong>Diffusion:</strong> Relocation (The Diaspora—forced dispersion by Romans/Babylonians)</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-8",
      title: "Topic 3.8: Effects of Diffusion",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Acculturation</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-slate-700">When a less dominant culture adopts some traits of the more influential one but maintains distinct cultural markers (language, food, customs).</p>
              <p className="text-xs text-slate-600 mt-2"><strong>Example:</strong> An immigrant family in the US speaks English at work but their native language at home</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Assimilation</h3>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <p className="text-sm text-slate-700">The process through which a culture loses its differentiating traits and is fully absorbed into the dominant culture.</p>
              <p className="text-xs text-slate-600 mt-2"><strong>Forced Assimilation:</strong> Government policies (U.S. and Canadian residential schools forcing Indigenous children to cut hair and stop speaking native languages)</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Syncretism</h3>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <p className="text-sm text-slate-700">The blending of traits from two different cultures to form a new cultural feature.</p>
              <ul className="text-xs text-slate-600 space-y-1 list-disc pl-4 mt-2">
                <li><strong>Religion:</strong> Santeria (African traditional beliefs + Roman Catholicism)</li>
                <li><strong>Food:</strong> Tex-Mex cuisine</li>
                <li><strong>Holidays:</strong> Day of the Dead (Indigenous Aztec rituals + Catholic All Souls' Day)</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Multiculturalism</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm text-slate-700">The coexistence of several cultures in one society, with the ideal of all cultures being valued and worthy of preservation.</p>
              <ul className="text-xs text-slate-600 space-y-1 list-disc pl-4 mt-2">
                <li><strong>Metaphor:</strong> The "Salad Bowl" (distinct ingredients) vs. Assimilation's "Melting Pot"</li>
                <li><strong>Conflict:</strong> Can lead to tension if the dominant group feels their identity is threatened (xenophobia)</li>
              </ul>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default humanGeographyUnit3Notes;